import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Menu, X, ChevronDown, Sparkle, Clock4, ArrowRight, ShieldCheck, LogOut } from "lucide-react";
import { SITE } from "@/config";
import MegaMenu from "./MegaMenu";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Free Kundli", to: "/free-kundli" },
  { label: "Consultation", to: "/the-call-consultation" },
  { label: "Reports", to: "/reports" },
  { label: "Blogs", to: "/blogs" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mega, setMega] = useState(false);
  const [adminAuthed, setAdminAuthed] = useState(false);
  const { pathname } = useLocation();

  // Super-admin session check whenever the right toggle menu opens.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        let r = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!r.ok) {
          const re = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
          if (re.ok) r = await fetch(`${API}/auth/me`, { credentials: "include" });
        }
        if (alive) setAdminAuthed(r.ok);
      } catch (_) {
        if (alive) setAdminAuthed(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const copyCode = () => {
    if (navigator.clipboard) navigator.clipboard.writeText("Shiva15");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <header data-testid="site-navbar" className="fixed top-0 left-0 right-0 z-50 shadow-[0_10px_30px_-18px_rgba(60,35,5,0.5)]">
      {/* MOBILE OFFER STRIP */}
      <div className="lg:hidden relative bg-[#7E451A] text-[#FDF3E3] overflow-hidden" data-testid="nav-offer-strip">
        <div className="flex w-max animate-marquee items-center h-8" style={{ animationDuration: "16s" }}>
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0 items-center" aria-hidden={dup === 1}>
              {["🪔 Navratri Offer — NEW", "Get FLAT 15% off on orders above ₹10,000", "Use Code: Shiva15"].map((t, i) => (
                <span key={i} className="flex items-center gap-2 px-4 text-[11px] font-bold tracking-wide whitespace-nowrap">
                  {t}
                  <Sparkle size={10} className="text-[#F5BC5C] shrink-0" fill="currentColor" />
                </span>
              ))}
            </div>
          ))}
        </div>
        <button
          onClick={copyCode}
          data-testid="nav-copy-code-button-mobile"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold bg-[#5F3A12]/90 border border-[#F2B24C]/50 text-[#FDF3E3] rounded-md px-2 py-1"
          aria-label="Copy coupon code Shiva15"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="h-12 lg:h-16 flex items-center bg-[#A65F1F]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" aria-label="Messenger of Shiva home" className="flex items-center">
            <img
              src="/brand-logo.webp"
              alt="Messenger of Shiva"
              data-testid="nav-logo-image"
              className="h-11 lg:h-[60px] w-auto object-contain"
            />
          </Link>

          <div data-testid="nav-partner-marks" className="flex items-center gap-2.5 sm:gap-4 shrink-0">
            <img
              src="/astrokuber-mark.webp"
              alt="A subsidiary of AstroKuber"
              loading="lazy"
              decoding="async"
              data-testid="nav-astrokuber-mark"
              className="h-11 lg:h-16 w-auto object-contain"
            />
            <span className="h-11 lg:h-16 w-px bg-white/25 shrink-0" aria-hidden="true" />
            <img
              src="/legacy-badge.webp"
              alt="100+ Years of Astrology Legacy"
              loading="lazy"
              decoding="async"
              data-testid="nav-legacy-badge"
              className="h-11 lg:h-16 w-auto object-contain"
            />
          </div>

          <p className="hidden lg:flex items-center gap-2.5 text-[15px] font-bold tracking-wide text-white" data-testid="nav-offer-text">
            Get FLAT 15% off on orders above ₹10,000&nbsp;&nbsp;|&nbsp;&nbsp;Use Code: Shiva15
            <button
              onClick={copyCode}
              data-testid="nav-copy-code-button"
              className="inline-flex items-center text-[12px] font-semibold bg-white/15 hover:bg-white/25 border border-white/40 rounded-md px-2 py-1 transition-colors"
              aria-label="Copy coupon code"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </p>

          <div className="flex items-center gap-3">
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-festive-pill"
              className="hidden lg:inline-flex items-center gap-2 text-white text-sm font-bold px-4 py-1.5 rounded-full border-2 border-white/80 hover:bg-white/10 transition-colors"
            >
              Navratri Offer
              <span className="bg-[#7CB342] text-[#1C1917] text-[10px] font-extrabold px-1.5 py-0.5 rounded">NEW</span>
            </a>
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram-cta"
              className="hidden sm:inline-flex items-center gap-2 text-white text-sm font-bold px-4 py-1.5 rounded-full border-2 border-white/80 hover:bg-white/10 transition-colors"
            >
              <Instagram size={15} strokeWidth={2.4} />
              {SITE.handle}
            </a>
            <button
              className="lg:hidden p-2 rounded-lg text-white"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <nav className="hidden lg:flex h-14 items-center bg-[#FFFDF8]/95 backdrop-blur-md border-b border-[#F0DFC2] relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full flex items-center gap-8">
          {LINKS.map((l) =>
            l.to === "/the-call-consultation" ? (
              <div key={l.to} className="relative" onMouseEnter={() => setMega(true)} onMouseLeave={() => setMega(false)}>
                <button
                  onClick={() => setMega(!mega)}
                  data-testid="nav-link-consultation-dropdown"
                  className={`relative text-[14.5px] tracking-wide transition-colors duration-200 py-4 inline-flex items-center gap-1 ${
                    pathname === l.to || mega ? "text-[#EA580C] font-semibold" : "text-[#4A3418] hover:text-[#EA580C]"
                  }`}
                >
                  {l.label}
                  <ChevronDown size={14} className={`transition-transform duration-200 ${mega ? "rotate-180" : ""}`} />
                  {pathname === l.to && (
                    <motion.span layoutId="nav-underline" className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-full bg-[#EA580C]" />
                  )}
                </button>
                {mega && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18 }}
                    className="absolute left-1/2 -translate-x-1/2 top-full w-[780px] card-ornate rounded-2xl shadow-[0_30px_60px_-20px_rgba(86,20,24,0.35)] overflow-hidden"
                    data-testid="megamenu-panel"
                  >
                    <MegaMenu onNavigate={() => setMega(false)} />
                  </motion.div>
                )}
              </div>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`relative text-[14.5px] tracking-wide transition-colors duration-200 py-4 ${
                  pathname === l.to ? "text-[#EA580C] font-semibold" : "text-[#4A3418] hover:text-[#EA580C]"
                }`}
              >
                {l.label}
                {pathname === l.to && (
                  <motion.span layoutId="nav-underline" className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-full bg-[#EA580C]" />
                )}
              </Link>
            )
          )}
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-lenis-ignore
            className="lg:hidden overflow-y-auto overscroll-contain max-h-[calc(100dvh-80px)] bg-[#FFFDF8] border-b border-[#F0DFC2]"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  onClick={() => setOpen(false)}
                  className={`py-3 text-[15px] border-b border-[#F0DFC2] last:border-0 ${
                    pathname === l.to ? "text-[#EA580C] font-semibold" : "text-[#4A3418]"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/trusted-astrologer"
                data-testid="nav-mobile-myastrosaathi-link"
                onClick={() => setOpen(false)}
                className="mt-3 flex items-center gap-3 rounded-xl border border-[#E5B77E]/60 p-3"
                style={{ background: "linear-gradient(135deg,#561418 0%,#400F13 100%)" }}
              >
                <span className="w-9 h-9 rounded-full bg-[#E5B77E]/15 border border-[#E5B77E]/50 grid place-items-center shrink-0">
                  <Clock4 size={15} className="text-[#F5BC5C]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7CFC9B] opacity-70" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#7CFC9B]" />
                    </span>
                    <span className="font-cinzel text-[10px] tracking-[0.2em] uppercase text-[#F5BC5C] font-semibold">MyAstroSaathi 24/7</span>
                  </span>
                  <span className="block text-[13px] font-semibold text-[#FDF3E3] mt-0.5 leading-tight">Talk with Premium Elite Astrologers</span>
                </span>
                <ArrowRight size={15} className="text-[#F5BC5C] shrink-0" />
              </Link>
              <div className="mt-3 pt-3 border-t border-[#F0DFC2]" data-testid="nav-admin-section">
                {adminAuthed ? (
                  <>
                    <Link
                      to="/admin"
                      data-testid="nav-admin-purchases-link"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2.5 py-2.5 text-[14px] font-semibold text-[#561418]"
                    >
                      <ShieldCheck size={16} className="text-[#A65F1F]" /> User Recent Purchases — All
                    </Link>
                    <button
                      onClick={async () => {
                        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
                        setAdminAuthed(false);
                      }}
                      data-testid="nav-admin-logout"
                      className="flex items-center gap-2.5 py-2.5 text-[13px] text-[#8A7364]"
                    >
                      <LogOut size={15} /> Admin Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/admin"
                    data-testid="nav-admin-login-link"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2.5 py-2.5 text-[13px] font-semibold text-[#8A7364]"
                  >
                    <ShieldCheck size={15} /> Admin Login
                  </Link>
                )}
              </div>
              <div className="py-3 border-b border-[#F0DFC2]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#8A7364] font-semibold mb-3">Consultations</p>
                <div className="rounded-2xl border border-[#F0DFC2] overflow-hidden">
                  <MegaMenu onNavigate={() => setOpen(false)} />
                </div>
              </div>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-saffron mt-3 mb-2 inline-flex items-center justify-center gap-2 text-white text-sm font-semibold px-5 py-3 rounded-full"
                data-testid="nav-mobile-instagram-cta"
              >
                <Instagram size={15} strokeWidth={2.4} />
                Book on Instagram {SITE.handle}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
